import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../utils/api';
import { Loading, Badge } from '../../components/Shared';

const PRIORITES       = ['Haute', 'Normale', 'Basse'];
const STATUTS_DEFAUT  = ['Prospect', 'Client actif', 'Client inactif', 'Partenaire'];

const PRIORITE_COLOR = {
  Haute:   { bg: 'rgba(248,113,113,0.15)', color: '#f87171' },
  Normale: { bg: 'rgba(92,96,120,0.2)',    color: '#b8bdd0' },
  Basse:   { bg: 'rgba(74,158,255,0.12)',  color: '#4a9eff' },
};

function ScoreBadge({ score }) {
  const s = parseInt(score || 0);
  const color = s >= 70 ? '#34d399' : s >= 40 ? '#f59e0b' : '#f87171';
  return (
    <div className="crm-score-bar" title={`Score engagement : ${s}/100`}>
      <div className="crm-score-fill" style={{ width: `${s}%`, background: color }} />
      <span className="crm-score-label">{s}</span>
    </div>
  );
}

function ModalCRMClient({ client, canaux, commerciaux, onSave, onClose }) {
  const [form, setForm] = useState({
    secteur_activite:       client.secteur_activite      || '',
    taille_entreprise:      client.taille_entreprise     || '',
    priorite_commerciale:   client.priorite_commerciale  || 'Normale',
    score_engagement:       client.score_engagement      || 0,
    optin_email:            client.optin_email           || false,
    date_prochain_contact:  client.date_prochain_contact || '',
    commercial_id:          client.commercial_id         || '',
    origine_contact_id:     client.origine_contact_id    || '',
    date_premier_contact:   client.date_premier_contact  || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const inputStyle = {
    width: '100%', padding: '0.45rem 0.65rem', borderRadius: '6px',
    border: '1px solid var(--border)', background: 'var(--bg-elevated)',
    color: 'var(--text-primary)', fontSize: '0.88rem', boxSizing: 'border-box',
  };

  async function handleSave() {
    setSaving(true); setError('');
    try {
      await apiFetch(`/crm/clients/${client.client_id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          ...form,
          score_engagement:   parseInt(form.score_engagement) || 0,
          commercial_id:      form.commercial_id      || null,
          origine_contact_id: form.origine_contact_id || null,
          date_prochain_contact: form.date_prochain_contact || null,
          date_premier_contact:  form.date_premier_contact  || null,
        }),
      });
      onSave();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" style={{ width: 520 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Informations CRM — {client.nom_client}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {error && <div className="form-error">{error}</div>}

          <div className="form-grid-2">
            <div>
              <label className="form-label">Secteur d'activité</label>
              <input style={inputStyle} value={form.secteur_activite}
                onChange={e => set('secteur_activite', e.target.value)} />
            </div>
            <div>
              <label className="form-label">Taille entreprise</label>
              <select style={inputStyle} value={form.taille_entreprise}
                onChange={e => set('taille_entreprise', e.target.value)}>
                <option value="">—</option>
                {['TPE','PME','ETI','GE','Particulier'].map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Priorité commerciale</label>
              <select style={inputStyle} value={form.priorite_commerciale}
                onChange={e => set('priorite_commerciale', e.target.value)}>
                {PRIORITES.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Score engagement (0–100)</label>
              <input type="number" min="0" max="100" style={inputStyle}
                value={form.score_engagement}
                onChange={e => set('score_engagement', e.target.value)} />
            </div>
            <div>
              <label className="form-label">Canal d'origine</label>
              <select style={inputStyle} value={form.origine_contact_id || ''}
                onChange={e => set('origine_contact_id', e.target.value)}>
                <option value="">—</option>
                {canaux.map(c => <option key={c.id} value={c.id}>{c.libelle}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Commercial référent</label>
              <select style={inputStyle} value={form.commercial_id || ''}
                onChange={e => set('commercial_id', e.target.value)}>
                <option value="">—</option>
                {commerciaux.map(u => (
                  <option key={u.id} value={u.id}>{u.prenom} {u.nom}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Prochain contact</label>
              <input type="date" style={inputStyle} value={form.date_prochain_contact || ''}
                onChange={e => set('date_prochain_contact', e.target.value)} />
            </div>
            <div>
              <label className="form-label">1er contact</label>
              <input type="date" style={inputStyle} value={form.date_premier_contact || ''}
                onChange={e => set('date_premier_contact', e.target.value)} />
            </div>
          </div>

          <div style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input type="checkbox" id="optin" checked={!!form.optin_email}
              onChange={e => set('optin_email', e.target.checked)} />
            <label htmlFor="optin" style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', cursor: 'pointer' }}>
              Opt-in email marketing accepté
            </label>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CRMClients() {
  const navigate   = useNavigate();
  const [clients, setClients]     = useState([]);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(1);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [search, setSearch]       = useState('');
  const [statut, setStatut]       = useState('');
  const [priorite, setPriorite]   = useState('');
  const [aRelancer, setARelancer] = useState(false);
  const [canaux, setCanaux]       = useState([]);
  const [commerciaux, setCommerciaux] = useState([]);
  const [editing, setEditing]     = useState(null);
  const [statuts, setStatuts]     = useState(STATUTS_DEFAUT);

  const LIMIT = 30;

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({
      page, limit: LIMIT,
      ...(search    && { search }),
      ...(statut    && { statut }),
      ...(priorite  && { priorite }),
      ...(aRelancer && { a_relancer: 'true' }),
    });
    apiFetch(`/crm/clients?${params}`)
      .then(r => { setClients(r.data); setTotal(r.total); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [page, search, statut, priorite, aRelancer]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    apiFetch('/crm/canaux-source').then(r => setCanaux(r.data)).catch(() => {});
    apiFetch('/utilisateurs').then(r => setCommerciaux(r.data || r)).catch(() => {});
    apiFetch('/client-options').then(r => { if (r.statuts) setStatuts(r.statuts); }).catch(() => {});
  }, []);

  const formatDate = d => d ? new Date(d).toLocaleDateString('fr-FR') : '—';
  const isOverdue  = d => d && new Date(d) < new Date();

  return (
    <div className="crm-section-page">
      {/* Filtres */}
      <div className="crm-filters">
        <input className="crm-search" placeholder="Rechercher…" value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }} />
        <select className="crm-select" value={statut}
          onChange={e => { setStatut(e.target.value); setPage(1); }}>
          <option value="">Tous les statuts</option>
          {statuts.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="crm-select" value={priorite}
          onChange={e => { setPriorite(e.target.value); setPage(1); }}>
          <option value="">Toutes priorités</option>
          {PRIORITES.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <label className="crm-checkbox-label">
          <input type="checkbox" checked={aRelancer}
            onChange={e => { setARelancer(e.target.checked); setPage(1); }} />
          À relancer
        </label>
      </div>

      {loading && <Loading />}
      {error && <div className="crm-error">{error}</div>}

      {!loading && !error && (
        <>
          <div className="crm-table-wrap">
            <table className="crm-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Statut</th>
                  <th>Priorité</th>
                  <th>Engagement</th>
                  <th>Prochain contact</th>
                  <th>Commercial</th>
                  <th>Opportunités</th>
                  <th>Interactions</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {clients.length === 0 && (
                  <tr><td colSpan={9} className="crm-empty-row">Aucun client trouvé</td></tr>
                )}
                {clients.map(c => {
                  const pc = PRIORITE_COLOR[c.priorite_commerciale] || PRIORITE_COLOR.Normale;
                  return (
                    <tr key={c.client_id} className="crm-table-row">
                      <td>
                        <button className="crm-link" onClick={() => navigate(`/clients/${c.client_id}`)}>
                          {c.nom_client}
                        </button>
                        {c.secteur_activite && (
                          <div className="crm-sub">{c.secteur_activite}</div>
                        )}
                      </td>
                      <td>
                        <span className="crm-badge" style={{
                          background: c.statut_client === 'Client actif'
                            ? 'rgba(52,211,153,0.15)' : 'rgba(74,158,255,0.12)',
                          color: c.statut_client === 'Client actif' ? '#34d399' : '#4a9eff',
                        }}>
                          {c.statut_client}
                        </span>
                      </td>
                      <td>
                        <span className="crm-badge" style={pc}>
                          {c.priorite_commerciale || 'Normale'}
                        </span>
                      </td>
                      <td><ScoreBadge score={c.score_engagement} /></td>
                      <td style={{ color: isOverdue(c.date_prochain_contact) ? '#f87171' : 'inherit' }}>
                        {formatDate(c.date_prochain_contact)}
                        {isOverdue(c.date_prochain_contact) && <span> ⚠</span>}
                      </td>
                      <td className="crm-text-sm">{c.commercial_nom || '—'}</td>
                      <td className="crm-text-center">{c.nb_opportunites || 0}</td>
                      <td className="crm-text-center">{c.nb_interactions || 0}</td>
                      <td>
                        <button className="crm-btn-edit"
                          onClick={() => setEditing(c)}
                          title="Modifier infos CRM">
                          ✎
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {total > LIMIT && (
            <div className="crm-pagination">
              <button disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
              <span>Page {page} / {Math.ceil(total / LIMIT)}</span>
              <button disabled={page >= Math.ceil(total / LIMIT)} onClick={() => setPage(p => p + 1)}>›</button>
            </div>
          )}
          <div className="crm-count">{total} client{total > 1 ? 's' : ''}</div>
        </>
      )}

      {editing && (
        <ModalCRMClient
          client={editing}
          canaux={canaux}
          commerciaux={commerciaux}
          onSave={() => { setEditing(null); load(); }}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
